import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Field, withTypes } from 'react-final-form';
import CurrencyFormat from 'react-number-format';
import { useDispatch } from 'react-redux';
import { Col, Row } from 'reactstrap';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { useDict } from '../../../contexts/LanguageContext';

// import { postAllocateAmount } from '../src/investNowApis';
import CustomInputField from '../../../components/form/CustomInputField';
import Modal from '../../../components/modal/Modal';
import getApiError from '../../../utils/getApiError';
import { normalizeCurrency, parseAmountFloat } from '../../../utils/helper';

const InvestFormModal = (props) => {
    const dict = useDict();
    const { toggle, isOpen, selectedInvestmentNote } = props;
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const [isLoading, setIsLoading] = useState(false);

    const postAllocateAmount = () => {
        return null;
    }

    const submitInvestSuccess = () => {
        return null;
    }

    const submitInvestFailed = () => {
        return null;
    }

    const { mutate } = useMutation(postAllocateAmount, {
        onSuccess: () => {
            queryClient.invalidateQueries(['getCurrentInvoice']);
            queryClient.invalidateQueries(['e-wallet-balance']);
            queryClient.invalidateQueries('portfolio-summary');
            queryClient.invalidateQueries('profile/summary-investment');
            dispatch(submitInvestSuccess());
            toggle();
        },
        onError: (error) => {
            const { response } = error;
            const { data } = response.data;
            let errorMsg = '';
            if (
                getApiError(data, 'amount').match(
                    /current balance is not enough*./
                )
            ) {
                errorMsg =
                    'Current balance is insufficient. Please top up into your ' +
                    'account to continue setting desired target!';
            } else if (
                getApiError(data, 'investment_note_id').match(
                    /investment_note_id exceeds the amount expected*/
                )
            ) {
                errorMsg = 'Invest amount exceeded available amount.';
            } else if (
                getApiError(data, 'investment_note_id').match(
                    /investment_note_id cannot make the available balance lower*/
                )
            ) {
                errorMsg =
                    'Remaining balance of investment note cannot lower than RM1000.';
            } else {
                errorMsg =
                    'Opps! Something wrong here. Please contact administrator.';
            }
            dispatch(submitInvestFailed(errorMsg));
        },
    });

    const { Form } = withTypes();

    const handleSubmit = (values) => {
        setIsLoading(true);
        const floatAmount = parseAmountFloat(values.amount.substr(3));

        mutate({
            amount: floatAmount,
            investment_note_id: parseInt(values.investmentNoteId, 10),
        });
        setIsLoading(false);
    };

    if (!selectedInvestmentNote) {
        return null;
    }

    const investValidation = (values) => {
        const errors = {};

        if (!values.amount) {
            errors.amount = 'Please enter a minimum amount of RM1,000.00';
        }

        if (values.amount) {
            const floatAmount = parseAmountFloat(values.amount.substr(3));
            if (floatAmount < 1000) {
                errors.amount = 'Please enter a minimum amount of RM1,000.00';
            }
        } else {
            errors.amount = 'Please enter a minimum amount of RM1,000.00';
        }

        return errors;
    };

    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
            title="Invest Amount"
            testID="investFormModal"
            headerType="review"
            cancelText={dict.close}
            onCancel={toggle}
            submitText={dict.confirm}
            submitBtnProps={{
                form: 'invest-form',
                disabled: isLoading,
            }}
            submitShown
        >
            <div className="modal__body pt-2">
                <Form
                    initialValues={{
                        amount: '',
                        investmentNoteId:
                            selectedInvestmentNote.investmentNoteId,
                    }}
                    onSubmit={(values) => handleSubmit(values)}
                    validate={investValidation}
                >
                    {({ handleSubmit }) => (
                        <form
                            id="invest-form"
                            className="form form-custom"
                            onSubmit={handleSubmit}
                        >
                            <Row className="mb-3">
                                <Col
                                    md={6}
                                    sm={12}
                                    xs={6}
                                    className="text-bold text-left"
                                >
                                    Investment Note:
                                </Col>
                                <Col
                                    md={6}
                                    sm={12}
                                    xs={6}
                                    className="text-right"
                                >
                                    {selectedInvestmentNote.investmentNoteName}
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col
                                    md={6}
                                    sm={12}
                                    xs={6}
                                    className="text-bold text-left"
                                >
                                    Available Amount:
                                </Col>
                                <Col
                                    md={6}
                                    sm={12}
                                    xs={6}
                                    className="text-right"
                                    data-private
                                >
                                    <CurrencyFormat
                                        value={parseFloat(
                                            selectedInvestmentNote.availableAmount
                                        )}
                                        displayType="text"
                                        thousandSeparator
                                        prefix="฿ "
                                    />
                                </Col>
                            </Row>
                            <Field name="investment_note_id">
                                {({ input, meta }) => (
                                    <CustomInputField
                                        type="hidden"
                                        input={input}
                                        meta={meta}
                                        style={{}}
                                    />
                                )}
                            </Field>
                            <Field name="amount" parse={normalizeCurrency}>
                                {({ input, meta }) => (
                                    <div className="form__form-group">
                                        <div
                                            className="form__form-group-field"
                                            data-private
                                        >
                                            <CustomInputField
                                                type="text"
                                                input={input}
                                                meta={meta}
                                                placeholder="Invest Amount (Minimum RM1,000.00)"
                                                mask={createNumberMask({
                                                    allowDecimal: true,
                                                    prefix: '฿ ',
                                                })}
                                            />
                                        </div>
                                    </div>
                                )}
                            </Field>
                        </form>
                    )}
                </Form>
            </div>
        </Modal>
    );
};

export default InvestFormModal;
